// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/cofre/EnviarXmlsBotao.tsx
//  Envio manual de XMLs ao Cofre Fiscal (.xml ou .zip) e o resumo do que entrou. Usado
//  no Portal do Contador e na Central Fiscal do ERP — quem usa passa a função de envio.
// ════════════════════════════════════════════════════════════════════════════

import { useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export interface ResumoEnvio {
  arquivos: number;
  novos: number;
  atualizados: number;
  duplicados: number;
  aguardando: number;
  ignorados: number;
  erros: number;
  problemas: { arquivo: string; resultado: string; motivo?: string | null }[];
}

interface Props {
  enviar: (arquivo: File) => Promise<ResumoEnvio>;
  /** Depois de um envio com algo novo (para recarregar as listas). */
  aoConcluir?: () => void;
  rotulo?: string;
}

export function EnviarXmlsBotao({ enviar, aoConcluir, rotulo = 'Enviar XMLs' }: Props) {
  const entrada = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [resumo, setResumo] = useState<ResumoEnvio | null>(null);

  const escolher = async (arquivo?: File) => {
    if (!arquivo) return;
    setEnviando(true);
    try {
      const r = await enviar(arquivo);
      setResumo(r);
      if (r.novos + r.atualizados + r.aguardando > 0) aoConcluir?.();
    } catch (e) {
      toast.error('Não foi possível enviar', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setEnviando(false);
      if (entrada.current) entrada.current.value = '';
    }
  };

  return (
    <>
      <input ref={entrada} type="file" accept=".xml,.zip" className="hidden" onChange={(e) => escolher(e.target.files?.[0])} />
      <Button variant="outline" onClick={() => entrada.current?.click()} disabled={enviando} className="gap-2">
        {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {enviando ? 'Enviando…' : rotulo}
      </Button>

      <Dialog open={resumo !== null} onOpenChange={(aberto) => !aberto && setResumo(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Resultado do envio</DialogTitle>
            <DialogDescription>{resumo?.arquivos ?? 0} XML(s) lido(s).</DialogDescription>
          </DialogHeader>
          {resumo && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Linha rotulo="Novos" valor={resumo.novos} />
                <Linha rotulo="Atualizados" valor={resumo.atualizados} />
                <Linha rotulo="Já estavam no cofre" valor={resumo.duplicados} />
                <Linha rotulo="Eventos sem a nota" valor={resumo.aguardando} />
                <Linha rotulo="Ignorados" valor={resumo.ignorados} />
                <Linha rotulo="Com erro" valor={resumo.erros} />
              </div>
              {resumo.problemas.length > 0 && (
                <div>
                  <p className="font-medium mb-1">O que não entrou</p>
                  <ul className="max-h-48 overflow-y-auto text-xs text-muted-foreground space-y-1">
                    {resumo.problemas.map((p, i) => (
                      <li key={i} className="break-all"><span className="font-mono">{p.arquivo}</span>: {p.motivo ?? p.resultado}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={() => setResumo(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div className="rounded-md border px-3 py-2">
      <p className="text-lg font-semibold">{valor}</p>
      <p className="text-xs text-muted-foreground">{rotulo}</p>
    </div>
  );
}
